import * as React from 'react';

import { Icon } from '@gitbook/spine-icons';
import { useLoadingState } from '@gitbook/util-react-hooks';

import { Button } from '../Button';
import { Description } from '../Description';
import { FieldGroup, Label } from '../Form';
import { LoadingIndicator } from '../Loading';
import { TextInput, type TextInputProps } from './TextInput';

type SubmitableTextInputProps = Omit<
    TextInputProps,
    'value' | 'onChange' | 'onSubmit' | 'trailing'
> & {
    value: string;
    submitLabel: string;
    onSubmit: (value: string) => Promise<void>;
    label?: string;
    labelDescription?: React.ReactNode;
    labelSelectable?: boolean;
};

export function SubmitableTextInput(props: SubmitableTextInputProps) {
    const {
        value: initialValue,
        submitLabel,
        onSubmit,
        label,
        labelDescription,
        labelSelectable,
        disabled,
        ...textInputProps
    } = props;

    const [value, setValue] = React.useState(initialValue);
    const [loading, wrapLoading] = useLoadingState();

    React.useEffect(() => {
        setValue(initialValue);
    }, [initialValue]);

    const isDirty = value !== initialValue;

    const handleSubmit = React.useCallback(() => {
        if (!isDirty) {
            return;
        }

        wrapLoading(async () => {
            await onSubmit(value);
        });
    }, [isDirty, onSubmit, value, wrapLoading]);

    return (
        <FieldGroup>
            {label ? (
                <Label className={labelSelectable ? 'select-text' : undefined}>{label}</Label>
            ) : null}
            {labelDescription ? <Description>{labelDescription}</Description> : null}
            <TextInput
                {...textInputProps}
                value={value}
                disabled={disabled || loading}
                onChange={(event) => setValue(event.target.value)}
                onSubmit={() => handleSubmit()}
                trailing={
                    isDirty ? (
                        <Button
                            label={submitLabel}
                            size="small"
                            kind="primary"
                            leadingIcon={loading ? LoadingIndicator : undefined}
                            trailingIcon={Icon.EnterKey}
                            onPress={() => handleSubmit()}
                            disabled={loading}
                        />
                    ) : null
                }
            />
        </FieldGroup>
    );
}
